import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	InputAdornment,
	IconButton,
	Alert,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useSnackbar } from "notistack";
import authService from "../services/authService";
import { ThemeToggle } from "../components/ThemeMode";

export default function ResetPassword() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token") || "";

	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);

	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		if (!token) {
			setError("Invalid reset link. Please request a new one.");
			return;
		}
		if (newPassword.length < 6) {
			setError("Password must be at least 6 characters.");
			return;
		}
		if (newPassword !== confirmPassword) {
			setError("Passwords do not match.");
			return;
		}

		setLoading(true);
		try {
			await authService.resetPassword(token, newPassword);
			setSuccess(true);
			enqueueSnackbar("Password updated successfully.", { variant: "success" });
		} catch (err) {
			const msg =
				err.response?.data?.message ||
				"Invalid or expired reset link. Please request a new one.";
			setError(msg);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: (theme) =>
					theme.palette.mode === "dark"
						? "radial-gradient(900px circle at 20% 0%, rgba(99, 102, 241, 0.22), transparent 60%), radial-gradient(700px circle at 90% 100%, rgba(8, 145, 178, 0.16), transparent 60%), #0b0d17"
						: "linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<ThemeToggle sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }} />
			<Box
				sx={{
					position: "absolute",
					width: "100%",
					height: "100%",
					opacity: 0.1,
					pointerEvents: "none",
					background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C%2Fg%3E%3C%2Fg%3E%3C/svg%3E")`,
				}}
			/>

			{/* Positioned so the card stays above the pattern once the animation's transform is gone */}
			<motion.div
				style={{ position: "relative", zIndex: 1 }}
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.5 }}
			>
				<Card
					sx={(theme) => ({
						maxWidth: 450,
						width: "90vw",
						borderRadius: 4,
						boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
						overflow: "visible",
						...(theme.palette.mode === "dark" && {
							bgcolor: "#1a1d2e",
							border: "1px solid rgba(255, 255, 255, 0.1)",
							boxShadow: "0 30px 80px -20px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(129, 140, 248, 0.12)",
						}),
					})}
				>
					<CardContent sx={{ p: 5 }}>
						<Box sx={{ textAlign: "center", mb: 4 }}>
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 1.5,
									mb: 2,
									cursor: "pointer",
								}}
								onClick={() => navigate("/")}
							>
								<Box
									sx={{
										width: 40,
										height: 40,
										borderRadius: "8px",
										background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
										boxShadow: "0 2px 8px rgba(124, 58, 237, 0.35)",
									}}
								>
									<svg
										width="22"
										height="22"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											stroke="white"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</Box>
								<Box sx={{ position: "relative", display: "inline-block" }}>
									<Typography
										variant="h5"
										fontWeight={800}
										sx={{
											background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 55%, #a855f7 100%)",
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent",
											backgroundClip: "text",
											letterSpacing: "-0.02em",
											lineHeight: 1.2,
										}}
									>
										Testify
									</Typography>
									<Typography
										sx={{
											position: "absolute",
											top: -5,
											right: -2,
											fontSize: "7px",
											fontWeight: 700,
											color: "#7c3aed",
											letterSpacing: "0.07em",
											textTransform: "uppercase",
											lineHeight: 1,
											opacity: 0.85,
										}}
									>
										SAARKA's
									</Typography>
								</Box>
							</Box>
							<Typography variant="h4" fontWeight={700} gutterBottom>
								Set new password
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Choose a strong password for your account.
							</Typography>
						</Box>

						{success ? (
							<Box sx={{ textAlign: "center" }}>
								<Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
									Your password has been updated successfully.
								</Alert>
								<Button
									variant="contained"
									fullWidth
									size="large"
									onClick={() => navigate("/login")}
									sx={{
										py: 1.5,
										background: "#4f46e5",
										"&:hover": { background: "#3730a3" },
									}}
								>
									Sign In
								</Button>
							</Box>
						) : (
							<Box component="form" onSubmit={handleSubmit} noValidate>
								{!token && (
									<Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
										Invalid reset link. Please{" "}
										<Button
											variant="text"
											size="small"
											onClick={() => navigate("/login")}
											sx={{ p: 0, minWidth: 0, textTransform: "none" }}
										>
											request a new one
										</Button>
										.
									</Alert>
								)}

								{error && (
									<Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
										{error}
									</Alert>
								)}

								<TextField
									margin="normal"
									required
									fullWidth
									label="New Password"
									type={showNew ? "text" : "password"}
									autoFocus
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<Lock color="action" />
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => setShowNew(!showNew)}
													edge="end"
												>
													{showNew ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
								<TextField
									margin="normal"
									required
									fullWidth
									label="Confirm New Password"
									type={showConfirm ? "text" : "password"}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<Lock color="action" />
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={() => setShowConfirm(!showConfirm)}
													edge="end"
												>
													{showConfirm ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									size="large"
									disabled={loading || !token}
									sx={{
										mt: 3,
										mb: 2,
										py: 1.5,
										background: "#4f46e5",
										"&:hover": { background: "#3730a3" },
									}}
								>
									{loading ? "Updating..." : "Update Password"}
								</Button>

								<Box sx={{ textAlign: "center" }}>
									<Button
										variant="text"
										size="small"
										onClick={() => navigate("/login")}
										sx={{ color: "text.secondary", textTransform: "none" }}
									>
										Back to Sign In
									</Button>
								</Box>
							</Box>
						)}
					</CardContent>
				</Card>
			</motion.div>
		</Box>
	);
}
